import type { ReactNode } from 'react';

interface CircularGaugeProps {
  value: number | null;
  max: number;
  size: number;
  strokeWidth?: number;
  color?: string;
  trackColor?: string;
  muted?: boolean;
  children?: ReactNode;
}

export default function CircularGauge({
  value,
  max,
  size,
  strokeWidth = 6,
  color = 'var(--accent)',
  trackColor = '#F0F0F2',
  muted = false,
  children,
}: CircularGaugeProps) {
  const safeValue = value ?? 0;
  const pct = Math.min(Math.max((safeValue / max) * 100, 0), 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - pct / 100);

  const effectiveColor = muted ? '#C8C8CC' : color;
  const effectiveTrack = muted ? '#F0F0F2' : trackColor;

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size}>
        {/* Track ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={effectiveTrack}
          strokeWidth={strokeWidth}
        />
        {/* Value arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={effectiveColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 600ms ease' }}
        />
      </svg>

      {/* Inner content */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center"
        style={{ padding: strokeWidth + 4 }}
      >
        {children}
      </div>
    </div>
  );
}